const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

/** Cached per zone: constructing an `Intl.DateTimeFormat` costs about a hundred times more than using one. */
function zoneFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = zoneFormatters.get(zone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    zoneFormatters.set(zone, formatter);
  }

  return formatter;
}

/** The wall clock `instant` reads in `zone`, expressed as a UTC timestamp (whole seconds). */
function tenantWallClockMillis(instant: Date, zone: string): number {
  const parts = zoneFormatter(zone).formatToParts(instant);
  const part = (type: string): number => Number(parts.find(p => p.type === type)?.value);

  return Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  );
}

/** Offset of `zone` at `instant`, in minutes east of UTC. */
function tenantOffsetInMinutes(instant: Date, zone: string): number {
  const wholeSeconds = Math.floor(instant.getTime() / MS_PER_SECOND) * MS_PER_SECOND;

  return Math.round((tenantWallClockMillis(instant, zone) - wholeSeconds) / MS_PER_MINUTE);
}

/** The instant whose wall clock in `zone` matches the local fields of `date`. */
export function tenantWallClockToUtc(date: Date, zone: string): Date {
  const wallClock = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );

  // A daylight-saving change can only fall between the day before and the day after, so these are the only
  // two instants the requested wall clock can map to.
  const offsetBefore = tenantOffsetInMinutes(new Date(wallClock - MS_PER_DAY), zone);
  const offsetAfter = tenantOffsetInMinutes(new Date(wallClock + MS_PER_DAY), zone);
  const withOffsetBefore = new Date(wallClock - offsetBefore * MS_PER_MINUTE);
  const withOffsetAfter = new Date(wallClock - offsetAfter * MS_PER_MINUTE);

  const requested = Math.floor(wallClock / MS_PER_SECOND) * MS_PER_SECOND;
  const beforeReadsBack = tenantWallClockMillis(withOffsetBefore, zone) === requested;
  const afterReadsBack = tenantWallClockMillis(withOffsetAfter, zone) === requested;

  if (beforeReadsBack !== afterReadsBack) {
    return beforeReadsBack ? withOffsetBefore : withOffsetAfter;
  }

  // Away from a transition both are the same instant. Otherwise the clock either ran the hour twice — the
  // earliest occurrence wins — or skipped it, where nothing reads back and going later keeps the value from
  // landing on the previous day.
  return new Date(
    beforeReadsBack
      ? Math.min(withOffsetBefore.getTime(), withOffsetAfter.getTime())
      : Math.max(withOffsetBefore.getTime(), withOffsetAfter.getTime()),
  );
}

/** The instant an outgoing `Date` is serialized as: its wall clock read in `zone`, or itself without one. */
export function toOutgoingInstant(date: Date, zone: string | null | undefined): Date {
  return zone ? tenantWallClockToUtc(date, zone) : new Date(date.getTime());
}

/**
 * Sends every outgoing `Date` as the instant its wall-clock fields represent in the tenant time zone
 * rather than the browser's, so a picked "08.03.2026 00:00" means the tenant's midnight everywhere.
 * Installed once by `AppComponent`; returns the previous `toJSON` restorer for specs.
 */
export function installTenantDateSerialization(zone: () => string | null | undefined): () => void {
  const previous = Date.prototype.toJSON;

  Date.prototype.toJSON = function (this: Date): string {
    // Native toJSON returns null here; throwing would break the whole payload.
    if (!Number.isFinite(this.getTime())) {
      return null as unknown as string;
    }

    return toOutgoingInstant(this, zone()).toISOString();
  };

  return () => {
    Date.prototype.toJSON = previous;
  };
}
